import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';

export const routes: Routes = [
  { path: '', redirectTo: 'app/modules', pathMatch: 'full' },

  // ── SSO landing (Website → App cross-domain auth) ──────────────────
  {
    path: 'sso',
    loadComponent: () =>
      import('./modules/auth/sso/sso.component').then(m => m.SsoComponent)
  },

  // ── Public invoice link (no auth required) ─────────────────────
  {
    path: 'invoice/:bookingNo',
    loadComponent: () =>
      import('./modules/booking/invoice-download/invoice-download.component')
        .then(m => m.InvoiceDownloadComponent)
  },

  // ── Public Auth routes (no shell, no guard) ────────────────────────
  {
    path: 'app/auth',
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./modules/auth/login/login.component').then(m => m.LoginComponent)
      },
      {
        path: 'register',
        loadComponent: () =>
          import('./modules/auth/register/register.component').then(m => m.RegisterComponent)
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./modules/auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent)
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./modules/auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent)
      },
      {
        path: 'mfa-verify',
        loadComponent: () =>
          import('./modules/auth/mfa-verify/mfa-verify.component').then(m => m.MfaVerifyComponent)
      },
      {
        path: 'device-verify',
        loadComponent: () =>
          import('./modules/auth/device-verify/device-verify.component').then(m => m.DeviceVerifyComponent)
      },
      {
        path: 'session-expired',
        loadComponent: () =>
          import('./modules/auth/session-expired/session-expired.component').then(m => m.SessionExpiredComponent)
      },
      {
        path: 'activate',
        loadComponent: () =>
          import('./modules/auth/account-activate/account-activate.component').then(m => m.AccountActivateComponent)
      },
      {
        path: 'otp-verify',
        loadComponent: () =>
          import('./modules/auth/otp-verify/otp-verify.component').then(m => m.OtpVerifyComponent)
      },
      {
        path: 'social-callback',
        loadComponent: () =>
          import('./modules/auth/social-callback/social-callback.component').then(m => m.SocialCallbackComponent)
      },
      { path: '', redirectTo: 'login', pathMatch: 'full' }
    ]
  },

  // ── App routes (shell + auth guard) ───────────────────────────────
  {
    path: 'app',
    loadComponent: () =>
      import('./layout/shell/shell.component').then(m => m.ShellComponent),
    canActivate: [authGuard],
    children: [
      {
        path: 'modules',
        loadComponent: () =>
          import('./modules/module-home/module-home.component').then(m => m.ModuleHomeComponent)
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./modules/dashboard/dashboard.component').then(m => m.DashboardComponent)
      },
      {
        path: 'dashboard/admin/game-overview',
        loadComponent: () =>
          import('./modules/dashboard/admin-game-dashboard/admin-game-dashboard.component')
            .then(m => m.AdminGameDashboardComponent)
      },
      {
        path: 'dashboard/admin/game/:sportId',
        loadComponent: () =>
          import('./modules/dashboard/admin-game-detail/admin-game-detail.component')
            .then(m => m.AdminGameDetailComponent)
      },
      {
        path: 'users',
        loadChildren: () =>
          import('./modules/user-management/user-management.routes').then(m => m.USER_ROUTES)
      },
      {
        path: 'booking',
        loadChildren: () =>
          import('./modules/booking/booking.routes').then(m => m.BOOKING_ROUTES)
      },
      {
        path: 'master-data',
        loadChildren: () =>
          import('./modules/master-data/master-data.routes').then(m => m.MASTER_ROUTES)
      },

      // ── Security / Account ────────────────────────────────────────
      {
        path: 'security/profile',
        loadComponent: () =>
          import('./modules/security/profile/profile.component').then(m => m.ProfileComponent)
      },
      {
        path: 'security/dashboard',
        loadComponent: () =>
          import('./modules/security/security-dashboard/security-dashboard.component').then(m => m.SecurityDashboardComponent)
      },
      {
        path: 'security/change-password',
        loadComponent: () =>
          import('./modules/security/change-password/change-password.component').then(m => m.ChangePasswordComponent)
      },
      {
        path: 'security/manage-devices',
        loadComponent: () =>
          import('./modules/security/manage-devices/manage-devices.component').then(m => m.ManageDevicesComponent)
      },
      {
        path: 'auth/mfa-setup',
        loadComponent: () =>
          import('./modules/auth/mfa-setup/mfa-setup.component').then(m => m.MfaSetupComponent)
      },
      {
        path: 'auth/active-sessions',
        loadComponent: () =>
          import('./modules/auth/active-sessions/active-sessions.component').then(m => m.ActiveSessionsComponent)
      },
      {
        path: 'auth/deactivate',
        loadComponent: () =>
          import('./modules/auth/account-deactivate/account-deactivate.component').then(m => m.AccountDeactivateComponent)
      },

      // ── Admin Security & Settings ──────────────────────────────────
      // /app/admin → redirects to audit-log; all sub-pages live in admin.routes.ts
      {
        path: 'admin',
        loadChildren: () =>
          import('./modules/admin/admin.routes').then(m => m.ADMIN_ROUTES)
      },
      {
        path: 'wallet',
        loadChildren: () =>
          import('./modules/wallet/wallet.routes').then(m => m.WALLET_ROUTES)
      },
      {
        path: 'finance',
        loadChildren: () =>
          import('./modules/finance/finance.routes').then(m => m.FINANCE_ROUTES)
      },
      {
        path: 'membership',
        loadChildren: () =>
          import('./modules/membership/membership.routes').then(m => m.MEMBERSHIP_ROUTES)
      },
      {
        path: 'reports',
        loadChildren: () => 
          import('./modules/reports/reports.routes').then(m => m.REPORTS_ROUTES)
      },
      {
        // Coaching & Training — a single module covering both the coaching
        // workflow and the Training Academy screens (programmes, categories,
        // fee ledger). There is deliberately no separate /app/training tree.
        path: 'coaching',
        loadChildren: () =>
          import('./modules/coaching/coaching.routes').then(m => m.COACHING_ROUTES)
      },
      {
        // Anything still pointing at the old /app/training/* paths lands in the
        // same module rather than 404ing.
        path: 'training',
        redirectTo: 'coaching',
      },
      {
        path: 'employee',
        loadChildren: () =>
          import('./modules/employee/employee.routes').then(m => m.EMPLOYEE_ROUTES)
      },
      {
        path: 'events',
        loadChildren: () =>
          import('./modules/events/events.routes').then(m => m.EVENTS_ROUTES)
      },
      {
        path: 'facility',
        loadChildren: () =>
          import('./modules/facility/facility.routes').then(m => m.FACILITY_ROUTES)
      },
      {
        path: 'inventory',
        loadChildren: () =>
          import('./modules/inventory/inventory.routes').then(m => m.INVENTORY_ROUTES)
      },
      {
        path: 'offers',
        loadChildren: () =>
          import('./modules/offers/offers.routes').then(m => m.OFFERS_ROUTES)
      },
      {
        path: 'tournament',
        loadChildren: () =>
          import('./modules/tournament/tournament.routes').then(m => m.TOURNAMENT_ROUTES)
      },
      {
        path: 'notifications',
        loadChildren: () =>
          import('./modules/notifications/notifications.routes').then(m => m.NOTIFICATION_ROUTES)
      },

      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },

      // Any unknown /app/... route → coming-soon inside shell (header + footer visible)
      {
        path: '**',
        loadComponent: () =>
          import('./shared/components/coming-soon/not-found-page.component').then(m => m.NotFoundShellComponent)
      }
    ]
  },

  // Fully unknown top-level URL → full-page 404
  { path: '**', loadComponent: () => import('./shared/components/coming-soon/not-found-page.component').then(m => m.NotFoundPageComponent) }
];

